import React from 'react'

import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import { Box, Button, Paper, TextField, Typography } from '@mui/material'
import { SxProps, Theme } from '@mui/system'

import { styles } from './PromptCarousel.styles'

export const TEST_ID = 'prompt-carousel'

const buttonStyle = {
	fontSize: '45px',
	color: 'rgba(69, 101, 123, 1)',
}

interface Item {
	description: string
	name: string
}

interface Button {
	label: string
}

const CarouselItem: React.FC<{ item: Item; sxOverrides?: SxProps<Theme> }> = ({
	item,
	sxOverrides,
}) => {
	return (
		<Box sx={styles.prevButtonSet}>
			<Paper
				sx={{
					padding: '2rem',
					backgroundColor: 'rgba(255, 232, 238, 1)',
					paddingX: {
						xs: '3rem',
						sm: '5rem',
					},
					...sxOverrides,
				}}
			>
				<Typography sx={styles.CarouselName}>{item?.name}</Typography>
				<Typography
					sx={styles.CarouselDescription}
					variant="h6"
					whiteSpace="pre-wrap"
				>
					{item?.description}
				</Typography>
			</Paper>
		</Box>
	)
}

const CarouselEditableItem: React.FC<{
	item: Item
	setPromptValue: React.Dispatch<string>
}> = ({ item, setPromptValue }) => {
	return (
		<Box sx={styles.prevButtonSet}>
			<Paper
				sx={{
					padding: '2rem',
					backgroundColor: '#FFFFFF',
					paddingX: {
						xs: '3rem',
						sm: '5rem',
					},
				}}
			>
				<Typography sx={styles.CarouselName}>{item.name}</Typography>
				<TextField
					inputProps={{
						'data-testid': 'carousel-edit-textfield',
					}}
					multiline
					fullWidth
					inputRef={(input) => input && input.focus()}
					sx={styles.CarouselEditableDescription}
					value={item.description}
					onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
						setPromptValue(event.target.value)
					}}
				/>
			</Paper>
		</Box>
	)
}

export const Carousel: React.FC<{
	currentIndex: number
	disabledArrows?: boolean
	editable: boolean
	editableItems: Item[]
	items: Item[]
	setCurrentIndex: React.Dispatch<React.SetStateAction<number>>
	setPromptValue: React.Dispatch<string>
	sxCardOverrides?: SxProps<Theme>
}> = ({
	items,
	editableItems,
	currentIndex,
	disabledArrows,
	editable,
	setCurrentIndex,
	setPromptValue,
	sxCardOverrides,
}) => {
	const handlePrev = () => {
		setCurrentIndex((prevIndex) =>
			prevIndex === 0 ? items.length - 1 : prevIndex - 1
		)
	}

	const handleNext = () => {
		setCurrentIndex((prevIndex) =>
			prevIndex === items.length - 1 ? 0 : prevIndex + 1
		)
	}

	return (
		<Box
			sx={{
				position: 'relative',
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
			}}
		>
			<Box sx={{ width: '100%', height: '100%' }}>
				{editable ? (
					<CarouselEditableItem
						item={editableItems[currentIndex]}
						setPromptValue={setPromptValue}
					/>
				) : (
					<CarouselItem
						item={items[currentIndex]}
						sxOverrides={sxCardOverrides}
					/>
				)}
			</Box>
			<Box
				sx={{
					...styles.prevButton,
					display: {
						xs: 'none',
						sm: 'block',
					},
				}}
			>
				<Button
					data-testid="prev-button"
					sx={{ ...styles.disabledButton }}
					style={{ height: '60px', minWidth: '60px' }}
					onClick={handlePrev}
					disabled={disabledArrows}
				>
					<NavigateBeforeIcon style={buttonStyle} />
				</Button>
			</Box>
			<Box
				sx={{
					...styles.nextButton,
					display: {
						xs: 'none',
						sm: 'block',
					},
				}}
			>
				<Button
					data-testid="next-button"
					sx={{ ...styles.disabledButton }}
					onClick={handleNext}
					style={{ height: '60px', width: '60px' }}
					disabled={disabledArrows}
				>
					<NavigateNextIcon style={buttonStyle} />
				</Button>
			</Box>
		</Box>
	)
}

const PromptCarousel: React.FC<{
	currentIndex: number
	currentPrompts: string[]
	disabledArrows?: boolean
	editable: boolean
	names?: string[]
	promptsEditMode: string[]
	setCurrentIndex: React.Dispatch<React.SetStateAction<number>>
	setPromptValue: React.Dispatch<string>
	sxCardOverrides?: SxProps<Theme>
}> = ({
	currentPrompts,
	promptsEditMode,
	currentIndex,
	disabledArrows,
	editable,
	setCurrentIndex,
	setPromptValue,
	sxCardOverrides,
	names = [
		'Industry Context',
		'Potential Challenges',
		'Testing Ideas',
		'Scale Opportunities',
	],
}) => {
	const items = currentPrompts.map((currentPrompt, i) => {
		return {
			name: names[i],
			description: currentPrompt,
		}
	})
	const editableItems = promptsEditMode.map((currentPrompt, i) => {
		return {
			name: names[i],
			description: currentPrompt,
		}
	})
	return (
		<Box>
			<Carousel
				items={items}
				editableItems={editableItems}
				currentIndex={currentIndex}
				setCurrentIndex={setCurrentIndex}
				setPromptValue={setPromptValue}
				editable={editable}
				sxCardOverrides={sxCardOverrides}
				disabledArrows={disabledArrows}
			/>
		</Box>
	)
}
export default PromptCarousel
